import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Send, ArrowLeft, MessageCircle, Plus } from "lucide-react";
import { API_BASE } from "../lib/api";
import UserSearch from "../components/UserSearch";

// Turn any /live/watch/<id>, /reels/<id>, /rooms/<id> path (with or without
// origin prefix) inside a plain message into a tappable Link. Keeps normal
// text as-is. Safe: only matches known internal routes.
const APP_PATH_RE = /(https?:\/\/[^\s]+)?(\/(?:live\/watch|reels|rooms|watch-party|live)\/[a-zA-Z0-9-_]+)/g;
function renderMessageContent(text, isMine) {
  if (!text) return null;
  const parts = [];
  let lastIdx = 0;
  let m;
  APP_PATH_RE.lastIndex = 0;
  while ((m = APP_PATH_RE.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    const path = m[2]; // internal path only (drop origin if present)
    parts.push(
      <Link
        key={`lnk-${m.index}`}
        to={path}
        data-testid="dm-inline-link"
        className={`underline font-bold break-all ${isMine ? "text-black" : "text-[#00E5FF]"}`}
      >{m[0]}</Link>
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts.length ? parts : text;
}

function ConvList() {
  const nav = useNavigate();
  const [convs, setConvs] = useState([]);
  const [showNew, setShowNew] = useState(false);
  useEffect(() => { api.get("/dm/conversations").then(r => setConvs(r.data)); }, []);
  return (
    <div className="px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Messages</h1>
        <button onClick={() => setShowNew(s => !s)} data-testid="dm-new"
          className="w-10 h-10 rounded-full bg-[#00E5FF] text-black flex items-center justify-center active:scale-90">
          <Plus className="w-5 h-5"/>
        </button>
      </div>

      {showNew && (
        <div className="mb-4 rounded-2xl bg-white/5 border border-white/10 p-3" data-testid="dm-new-search">
          <p className="text-xs text-white/60 mb-2">Find a user to message</p>
          <UserSearch onPick={u => { setShowNew(false); nav(`/dm/${u.id}`); }} pickLabel="Message" autoFocus />
        </div>
      )}

      {convs.length === 0 && !showNew && (
        <div className="text-center py-16 text-white/50">
          <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50"/>
          <p>No conversations yet</p>
          <p className="text-xs mt-1">Tap + to find someone to message</p>
        </div>
      )}
      <div className="space-y-2" data-testid="dm-conv-list">
        {convs.map(c => (
          <Link key={c.user.id} to={`/dm/${c.user.id}`} data-testid={`conv-${c.user.id}`}
            className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 active:scale-[0.98]">
            <img src={c.user.avatar} alt="" className="w-12 h-12 rounded-full border border-white/10"/>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">@{c.user.username}</div>
              <div className="text-xs text-white/60 truncate">{c.last_message}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Chat() {
  const { otherId } = useParams();
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [other, setOther] = useState(null);
  const wsRef = useRef(null);
  const endRef = useRef(null);

  // When the URL uses an alias like "admin", we resolve to the real user.id once loaded
  const realOtherId = other?.id || otherId;

  useEffect(() => {
    api.get(`/users/${otherId}`).then(r => setOther(r.data));
    api.get(`/dm/with/${otherId}`).then(r => setMsgs(r.data));
    const token = localStorage.getItem("hf_token");
    if (token) {
      const wsUrl = API_BASE.replace(/^http/, "ws") + `/ws/dm?token=${token}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === "message") {
            const d = m.data;
            const peer = other?.id || otherId;
            if ((d.from_user_id === peer && d.to_user_id === user.id) ||
                (d.from_user_id === user.id && d.to_user_id === peer)) {
              setMsgs(prev => prev.find(x => x.id === d.id) ? prev : [...prev, d]);
            }
          }
        } catch {}
      };
      return () => { try { ws.close(); } catch {} };
    }
  }, [otherId, user?.id, other?.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text;
    setText("");
    try {
      if (wsRef.current?.readyState === 1) {
        wsRef.current.send(JSON.stringify({ to_user_id: realOtherId, content: t }));
      } else {
        const { data } = await api.post("/dm/send", { to_user_id: realOtherId, content: t });
        setMsgs(p => [...p, data]);
      }
    } catch {}
  };

  return (
    <div className="flex flex-col h-screen pb-20">
      <div className="sticky top-0 z-10 glass border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <Link to="/dm" data-testid="dm-back"><ArrowLeft className="w-5 h-5"/></Link>
        {other && (
          <>
            <img src={other.avatar} alt="" className="w-9 h-9 rounded-full"/>
            <div>
              <div className="font-bold text-sm">{other.name}</div>
              <div className="text-xs text-white/60">@{other.username}</div>
            </div>
          </>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="dm-messages">
        {msgs.map(m => {
          const mine = m.from_user_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl ${mine ? "bg-[#00E5FF] text-black rounded-br-md" : "bg-white/10 rounded-bl-md"}`}>
                <div className="text-sm break-words whitespace-pre-wrap">{renderMessageContent(m.content, mine)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef}/>
      </div>
      <form onSubmit={send} className="px-3 py-2 border-t border-white/10 flex gap-2">
        <input data-testid="dm-input" value={text} onChange={e=>setText(e.target.value)} placeholder="Message..." className="flex-1 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 outline-none"/>
        <button data-testid="dm-send" className="w-11 h-11 rounded-full bg-[#00E5FF] flex items-center justify-center"><Send className="w-5 h-5 text-black"/></button>
      </form>
    </div>
  );
}

export default function DM() {
  const { otherId } = useParams();
  return otherId ? <Chat/> : <ConvList/>;
}
